import React from 'react';
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { ClassNames } from '../../../node_modules/@types/react-date-range/index.d';
import { ITotalSalesItem, PAYMETHOD, TRANSACTION } from '@dataTypes/totalSalesTypes';
import { formatPayMethodLabel } from '@utils/mileageDisplay';
import { useRecoilValue } from 'recoil';
import storeInfo from '../../recoil/storeInfo/atom';
import { IVanCancelErrorResponse, IVanCancelResponse } from '@dataTypes/vanCancelTypes';
import { UseMutateAsyncFunction, UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import Modal from 'react-bootstrap/esm/Modal';
import { TOTALSALES } from './queryKey/queryKey';
import { AUTH } from '@dataTypes/authTypes';
import Cookies from 'js-cookie';
import axios, { AxiosError } from 'axios';

interface SalesDetailModalProps {
  show: boolean;
  handleClose: () => void;
  detailModalData: ITotalSalesItem;
  showMileage?: boolean;
  vanCancelAsync: UseMutateAsyncFunction<
    IVanCancelResponse | IVanCancelErrorResponse,
    unknown,
    void,
    unknown
  >;
  vanCancelStatusData: boolean | undefined;
}

const SalesDetailModal = ({
  show,
  handleClose,
  detailModalData,
  showMileage = true,
  vanCancelAsync,
  vanCancelStatusData,
}: SalesDetailModalProps) => {
  const [showVanCancelModal, setShowVanCancelModal] = useState(false);
  const [errorText, setErrorText] = useState('');
  const storeInfoState = useRecoilValue(storeInfo);
  const queryClient = useQueryClient();
  const authority = Cookies.get('smartlab_authority');
  const formatTransactionDate = (transactionDate: string) => {
    const datePart = transactionDate.slice(0, 6); // "240601"
    const timePart = transactionDate.slice(6); // "000002"

    // 날짜 포맷팅
    const year = datePart.slice(0, 2); // "24"
    const month = datePart.slice(2, 4); // "06"
    const day = datePart.slice(4, 6); // "01"
    const formattedDate = `${year}-${month}-${day}`; // "24-06-01"

    // 시간 포맷팅
    const hours = timePart.slice(0, 2); // "00"
    const minutes = timePart.slice(2, 4); // "00"
    const seconds = timePart.slice(4, 6); // "02"
    const formattedTime = `${hours}:${minutes}:${seconds}`; // "00:00:02"

    return [formattedDate, formattedTime];
  };

  const [formattedDate, formattedTime] = formatTransactionDate(detailModalData?.transactionDate);

  const formattedTransaction = (method: TRANSACTION) => {
    switch (method) {
      case TRANSACTION.A:
        return '승인';
      case TRANSACTION.C:
        return '취소';
      case TRANSACTION.E:
        return '에러';
      default:
        return '알 수 없음';
    }
  };

  const addCommas = (number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleVanCancel = async () => {
    try {
      const result = await vanCancelAsync();
      if ('resultCode' in result && result.resultCode === '0000') {
        setShowVanCancelModal(false);
        queryClient.invalidateQueries({ queryKey: [TOTALSALES.VANCANCELSTATUS] });
      } else {
        setErrorText((result as IVanCancelResponse).resultMessage || '알 수 없는 오류');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.message || '퇴실 실패';
        setErrorText(errorMessage);
        console.error('Axios Error:', error.response?.data);
      } else {
        setErrorText('알 수 없는 오류');
        console.error('Unknown Error:', error);
      }
    }
  };

  const closeVanCancelModal = () => {
    setShowVanCancelModal(false);
    setErrorText('');
  };

  return (
    <>
      <Offcanvas
        id="sales_detail_modal_wrapper"
        show={show}
        onHide={handleClose}
        placement="bottom"
      >
        <Offcanvas.Header closeButton className="sales_detail_header">
          <Offcanvas.Title>상세보기</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="sales_detail_body">
          <div className="sales_detail_text_wrapper">
            <div className="sales_date">
              <span className="title">매출일자</span>
              <span className="text">{formattedDate}</span>
            </div>
            <div className="sales_date">
              <span className="title">매출시간</span>
              <span className="text">{formattedTime}</span>
            </div>
            <div className="sales_date time">
              <span className="title">지점명</span>
              <span className="text">{storeInfoState.currentStore}</span>
            </div>
            <div className="sales_date">
              <span className="title">결제수단</span>
              <span className="text">
                {formatPayMethodLabel(
                  showMileage,
                  detailModalData?.method || PAYMETHOD.CARD,
                  detailModalData.transactionStatus,
                )}
              </span>
            </div>
            <div className="sales_date">
              <span className="title">매입사</span>
              <span className="text">{detailModalData?.cardCompany}</span>
            </div>

            <div className="sales_date">
              <span className="title">승인번호</span>
              <span className="text">
                {(detailModalData && detailModalData.approvalNum) || '0'}
              </span>
            </div>

            <div className="sales_date info">
              <span className="title">카드번호</span>
              <span className="text">{detailModalData?.cardNum}</span>
            </div>
            <div className="sales_date">
              <span className="title">금액</span>
              <span className="text">
                {detailModalData && addCommas(detailModalData.amount || 0)}
              </span>
            </div>
            <div className="sales_date">
              <span className="title">부가세</span>
              <span className="text">{detailModalData && addCommas(detailModalData.tax || 0)}</span>
            </div>

            <div className="sales_date">
              <span className="title">단말기번호</span>
              <span className="text">{detailModalData?.catId}</span>
            </div>
            <div className="sales_date">
              <span className="title">거래유형</span>
              <span className="text">
                {(detailModalData && formattedTransaction(detailModalData?.transactionStatus)) ||
                  ''}
              </span>
            </div>
          </div>

          {detailModalData.method === PAYMETHOD.CARD && authority !== AUTH.USER && (
            <div className="bottom_btn_wrapper">
              <Button
                className="van_cancel_btn"
                onClick={() => setShowVanCancelModal(true)}
                disabled={!vanCancelStatusData}
              >
                {vanCancelStatusData ? '결제 취소' : '결제 취소 완료'}
              </Button>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={showVanCancelModal}
        onHide={closeVanCancelModal}
        size="lg"
        centered
        id={'van_cancel_modal_container'}
      >
        <Modal.Body>
          <div className="van_cancel_modal_text_wrapper">
            <p>정말 취소 하시겠습니까?</p>
            <div>취소 후 복구 불가능 합니다.</div>
          </div>
          <ul className="van_cancel_modal_list">
            <li>90일 이내의 거래건만 취소가 가능합니다.</li>
            <li>카드 거래만 취소가 가능합니다.</li>
          </ul>

          <div className="van_cancel_modal_error_text">{errorText}</div>
        </Modal.Body>
        <Modal.Footer className="van_cancel_modal_footer">
          <Button className="van_cancel_btn" onClick={() => handleVanCancel()}>
            확인
          </Button>
          <Button className="cancel_btn" onClick={() => setShowVanCancelModal(false)}>
            취소
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SalesDetailModal;
